// envPose — pure coercion of a stored environment default_pose into the exact
// shape the live scene consumes. No side effects, no store imports, so it is
// trivially unit-testable and reusable by every code path that reads a pose
// (applyEnvironment, LiveTunePanel dropdown, EnvironmentInspector).

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// Two non-obvious rules, both load-bearing:
//
//   - scale: a stored 0 (or blank / non-finite) is treated as 1. `?? 1` does
//     NOT catch a literal 0, and a 0 scale renders the splat invisible.
//     Clearing the Scale field in the inspector writes 0, so this guard keeps
//     a cleared field from silently hiding the environment.
//   - floorY: explicit avatar-floor plane. null = auto-detect from the splat
//     (the default, unchanged for every existing env). A finite value pins the
//     avatar's feet so centered/offset exports don't bury the character.
//
// For an empty pose ({}) this returns the same transform as before plus
// `floorY: null` — a strict no-op for studio/Messy and every shipped env.
export function normalizePose(rawPose) {
  let pose = rawPose
  if (typeof pose === 'string') {
    try { pose = JSON.parse(pose) } catch { pose = {} }
  }
  pose = pose ?? {}

  const rawScale = Number(pose.scale)
  const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1

  const rawFloorY = Number(pose.floorY)
  const floorY = pose.floorY == null || pose.floorY === '' || !Number.isFinite(rawFloorY)
    ? null
    : rawFloorY

  return {
    px: num(pose.px),
    py: num(pose.py),
    pz: num(pose.pz),
    rx: num(pose.rx),
    ry: num(pose.ry),
    rz: num(pose.rz),
    scale,
    floorY,
  }
}

// Field-wise pose comparison, used to answer "is this environment's live pose
// different from the one saved on the server?" — i.e. whether to show the
// unsaved-changes badge (ENG-895).
//
// Both sides are normalized first so a stored `{}`, a stored `{scale: 0}` and a
// live pose that has never been touched all compare equal instead of reading as
// permanently dirty. EPS absorbs float noise from a gizmo drag; anything a user
// could see is far larger.
const POSE_EPS = 1e-6
const POSE_KEYS = ['px', 'py', 'pz', 'rx', 'ry', 'rz', 'scale']

export function posesEqual(a, b) {
  const pa = normalizePose(a)
  const pb = normalizePose(b)
  for (const k of POSE_KEYS) {
    if (Math.abs(pa[k] - pb[k]) > POSE_EPS) return false
  }
  // floorY is nullable, so it can't go through the numeric compare above.
  if (pa.floorY == null || pb.floorY == null) return pa.floorY === pb.floorY
  return Math.abs(pa.floorY - pb.floorY) <= POSE_EPS
}
